import { useIsRpaRecorderEnabled, useIsWorkflowsEnabled } from '@/features/flags';
import { useUserWorkflows } from '@/features/rpa/recorder/hooks/useUserWorkflows';
import { deriveAvailableWorkflowSkillsFromSummaries } from '@/features/rpa/recorder/lib/derive-available-workflow-skills-from-summaries';
import { useWorkflows } from '@/features/workflows/hooks';
import { rpaBridge } from '@/tauri/rpa';
import type {
  UseRecordedWorkflowSkills,
  UseWorkflowSkillRoutines,
  WorkflowSkillWithKind,
} from '@heidi/agents/contracts';
import { useAvailableWorkflowSkillsWithWorkflowsCore } from '@heidi/agents/data';
import { useMemo } from 'react';

/** The Workflows verdict and the routine list, read through the app's gated hook. */
const useWorkflowSkillRoutines: UseWorkflowSkillRoutines = ({ enabled }) => {
  const isWorkflowsEnabled = useIsWorkflowsEnabled();
  const { data } = useWorkflows({}, { enabled });
  return { isWorkflowsEnabled, data };
};

/** The recorder's ready skills while it is on and the desktop bridge answers. */
const useRecordedWorkflowSkills: UseRecordedWorkflowSkills = ({ enabled }) => {
  const isRecorderEnabled = useIsRpaRecorderEnabled();
  const { data } = useUserWorkflows({ enabled });
  const rpaAvailable = isRecorderEnabled && rpaBridge.isAvailable();

  return useMemo(
    () =>
      rpaAvailable
        ? deriveAvailableWorkflowSkillsFromSummaries([
            ...(data?.personal ?? []),
            ...(data?.team ?? []),
          ])
        : [],
    [rpaAvailable, data]
  );
};

/** All enabled workflows (instructed + recorded) for the slash picker. */
export const useAvailableWorkflowSkillsWithWorkflows = (
  options: { enabled?: boolean } = {}
): readonly WorkflowSkillWithKind[] =>
  useAvailableWorkflowSkillsWithWorkflowsCore(
    useWorkflowSkillRoutines,
    useRecordedWorkflowSkills,
    options
  );
